import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiArrowLeft, FiEye, FiEyeOff } from 'react-icons/fi';

export default function LogisticRiderCreateAccount() {
  const [accepted, setAccepted] = useState(false);
  const [formValues, setFormValues] = useState({
    fullName: '',
    email: '',
    phone: '',
    state: '',
    city: '',
    address: '',
    courierMode: '',
    experience: '',
    numberOfFleets: '',
    accountName: '',
    bankName: '',
    accountNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [touched, setTouched] = useState({
    email: false,
    phone: false,
    confirmPassword: false,
  });
  const [submitState, setSubmitState] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const requiresFleets = formValues.courierMode === 'Fleet';
  const isComplete =
    accepted &&
    Object.entries(formValues).every(([key, value]) => {
      if (key === 'numberOfFleets') {
        return requiresFleets ? value.trim() !== '' : true;
      }
      return value.trim() !== '';
    });
  const emailInvalid =
    touched.email &&
    formValues.email.trim() !== '' &&
    !formValues.email.trim().toLowerCase().endsWith('@gmail.com');
  const phoneInvalid =
    touched.phone &&
    formValues.phone.trim() !== '' &&
    formValues.phone.replace(/\D/g, '').length > 11;
  const confirmPasswordInvalid =
    touched.confirmPassword &&
    formValues.confirmPassword.trim() !== '' &&
    formValues.confirmPassword !== formValues.password;

  const showMessage = (type, message) => {
    setSubmitState({ type, message });
    setTimeout(() => setSubmitState({ type: '', message: '' }), 2500);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const phoneDigits = formValues.phone.replace(/\D/g, '');
    if (!formValues.email.toLowerCase().endsWith('@gmail.com')) {
      showMessage('error', 'Email must include @gmail.com');
      return;
    }
    if (phoneDigits.length !== 11) {
      showMessage('error', 'Phone number must be 11 digits');
      return;
    }
    if (formValues.password.length < 8) {
      showMessage('error', 'Password must be at least 8 characters');
      return;
    }
    if (formValues.confirmPassword !== formValues.password) {
      showMessage('error', 'Confirm password do not match Password');
      return;
    }
    if (!accepted) {
      showMessage('error', 'Please accept Terms & Conditions');
      return;
    }
    if (requiresFleets && (!formValues.numberOfFleets || Number(formValues.numberOfFleets) <= 0)) {
      showMessage('error', 'Please input number of fleets');
      return;
    }

    const apiBase = process.env.REACT_APP_API_URL || 'https://buyit-backend-2.onrender.com';
    const courierModeValue = formValues.courierMode === 'Fleet' ? 'fleet' : 'single';
    const payload = {
      fullName: formValues.fullName,
      email: formValues.email,
      phoneNumber: phoneDigits,
      state: formValues.state,
      city: formValues.city,
      address: formValues.address,
      courierMode: courierModeValue,
      yearsOfExperience: Number(formValues.experience),
      bankName: formValues.bankName,
      accountNumber: formValues.accountNumber,
      accountName: formValues.accountName,
      password: formValues.password,
      confirmPassword: formValues.confirmPassword,
      termsAccepted: accepted,
    };
    if (requiresFleets) {
      payload.numberOfFleets = Number(formValues.numberOfFleets);
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${apiBase}/api/logistics/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Sign up failed');
      }
      showMessage('success', data.message || 'Sign up successful');
      setTimeout(() => navigate('/logistic-rider-login'), 800);
    } catch (err) {
      showMessage('error', err.message || 'Sign up failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F3F2] flex flex-col max-w-md mx-auto relative">
      {submitState.message && (
        <div
          className={`fixed top-4 left-1/2 z-50 -translate-x-1/2 rounded-lg px-6 py-3 text-sm font-semibold text-white shadow-lg ${
            submitState.type === 'success' ? 'bg-[#19C463]' : 'bg-[#FE0000]'
          }`}
          style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px' }}
        >
          {submitState.message}
        </div>
      )}
      {/* Header */}
      <div className="bg-[#F1F3F2] px-5 pb-4 pt-2 relative">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="absolute left-5 top-2 text-black"
          aria-label="Go back"
        >
          <FiArrowLeft size={22} />
        </button>
        <h1 className="text-base font-bold text-black text-center">Basic Account Details</h1>
      </div>

      {/* Main Content */}
      <div className="flex-1 bg-[#F1F3F2] px-5 pt-2 pb-8 overflow-y-auto">
        {/* Full Name */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            Full Name<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="text"
            placeholder="Full Name"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.fullName}
            onChange={(e) => setFormValues((prev) => ({ ...prev, fullName: e.target.value }))}
          />
        </div>

        {/* Email Address */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            Email Address<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="email"
            placeholder="Email Address"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.email}
            onChange={(e) => setFormValues((prev) => ({ ...prev, email: e.target.value }))}
            onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
          />
          {emailInvalid && (
            <p className="mt-1 text-xs font-medium text-red-500">
              Email must include @gmail.com
            </p>
          )}
        </div>

        {/* Phone Number */}
        <div className="mb-6">
          <label className="block text-black text-sm font-bold mb-1.5">
            Phone Number<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="tel"
            placeholder="Phone Number"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.phone}
            onChange={(e) => setFormValues((prev) => ({ ...prev, phone: e.target.value }))}
            onBlur={() => setTouched((prev) => ({ ...prev, phone: true }))}
          />
          {phoneInvalid && (
            <p className="mt-1 text-xs font-medium text-red-500">
              Please input a correct number (11 digits max)
            </p>
          )}
        </div>

        {/* Location Section */}
        <h2 className="text-base font-bold text-black text-center mb-4">Location</h2>

        {/* State */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            State<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="text"
            placeholder="State"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.state}
            onChange={(e) => setFormValues((prev) => ({ ...prev, state: e.target.value }))}
          />
        </div>

        {/* City */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            City<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="text"
            placeholder="City"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.city}
            onChange={(e) => setFormValues((prev) => ({ ...prev, city: e.target.value }))}
          />
        </div>

        {/* Address */}
        <div className="mb-6">
          <label className="block text-black text-sm font-bold mb-1.5">
            Address<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="text"
            placeholder="Address"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.address}
            onChange={(e) => setFormValues((prev) => ({ ...prev, address: e.target.value }))}
          />
        </div>

        {/* Delivery Details Section */}
        <h2 className="text-base font-bold text-black text-center mb-4">Delivery Details</h2>

        {/* Courier Mode */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            Courier Mode<span className="text-[#19C463]">*</span>
          </label>
          <select
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black outline-none"
            value={formValues.courierMode}
            onChange={(e) => setFormValues((prev) => ({ ...prev, courierMode: e.target.value }))}
          >
            <option value="" disabled>
              Select
            </option>
            <option value="Fleet">Fleet</option>
            <option value="Motorcycle">Motorcycle</option>
            <option value="Bus">Bus</option>
            <option value="Van">Van</option>
          </select>
        </div>

        {requiresFleets && (
          <div className="mb-4">
            <label className="block text-black text-sm font-bold mb-1.5">
              Number of Fleets<span className="text-[#19C463]">*</span>
            </label>
            <input
              type="number"
              min="1"
              placeholder="Number of Fleets"
              className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
              value={formValues.numberOfFleets}
              onChange={(e) => setFormValues((prev) => ({ ...prev, numberOfFleets: e.target.value }))}
            />
          </div>
        )}

        {/* Years of Experience */}
        <div className="mb-6">
          <label className="block text-black text-sm font-bold mb-1.5">
            Years of Experience<span className="text-[#19C463]">*</span>
          </label>
          <select
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black outline-none"
            value={formValues.experience}
            onChange={(e) => setFormValues((prev) => ({ ...prev, experience: e.target.value }))}
          >
            <option value="" disabled>
              Select
            </option>
            <option value="0-1">0-1 Year</option>
            <option value="2-4">2-4 Year</option>
            <option value="4-6">4-6 Year</option>
            <option value="6+">6+ years</option>
          </select>
        </div>

        {/* Bank Details Section */}
        <h2 className="text-base font-bold text-black text-center mb-4">Bank Details (for payouts)</h2>

        {/* Account Name */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            Account Name<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="text"
            placeholder="Account Name"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.accountName}
            onChange={(e) => setFormValues((prev) => ({ ...prev, accountName: e.target.value }))}
          />
        </div>

        {/* Bank Name */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            Bank Name<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="text"
            placeholder="Bank Name"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.bankName}
            onChange={(e) => setFormValues((prev) => ({ ...prev, bankName: e.target.value }))}
          />
        </div>

        {/* Account Number */}
        <div className="mb-6">
          <label className="block text-black text-sm font-bold mb-1.5">
            Account Number<span className="text-[#19C463]">*</span>
          </label>
          <input
            type="text"
            placeholder="Account Number"
            className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 text-sm text-black placeholder-gray-500 outline-none"
            value={formValues.accountNumber}
            onChange={(e) => setFormValues((prev) => ({ ...prev, accountNumber: e.target.value }))}
          />
        </div>

        {/* Password Section */}
        <h2 className="text-base font-bold text-black text-center mb-4">Password</h2>

        {/* Create Password */}
        <div className="mb-4">
          <label className="block text-black text-sm font-bold mb-1.5">
            Create Password<span className="text-[#19C463]">*</span>
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Create Password"
              className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 pr-12 text-sm text-black placeholder-gray-500 outline-none"
              value={formValues.password}
              onChange={(e) => setFormValues((prev) => ({ ...prev, password: e.target.value }))}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-black"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
        </div>

        {/* Confirm Password */}
        <div className="mb-8">
          <label className="block text-black text-sm font-bold mb-1.5">
            Confirm Password<span className="text-[#19C463]">*</span>
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm Password"
              className="w-full bg-[#F1F3F2] border-2 border-black rounded-md px-4 py-3 pr-12 text-sm text-black placeholder-gray-500 outline-none"
              value={formValues.confirmPassword}
              onChange={(e) => setFormValues((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              onBlur={() => setTouched((prev) => ({ ...prev, confirmPassword: true }))}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-black"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
            </button>
          </div>
          {confirmPasswordInvalid && (
            <p className="mt-1 text-xs font-medium text-red-500">
              Confirm password do not match Password
            </p>
          )}
        </div>

        {/* Terms & Conditions */}
        <div className="flex items-center gap-3 mb-8">
          <div
            onClick={() => setAccepted(!accepted)}
            className="w-6 h-6 border-2 border-[#19C463] rounded flex items-center justify-center cursor-pointer"
          >
            {accepted && <FiCheck size={16} color="#19C463" strokeWidth={3} />}
          </div>
          <span className="text-sm text-black">
            Accept <span className="text-[#19C463] font-medium">Terms & Conditions</span>
          </span>
        </div>

        {/* Complete Button */}
        <button
          disabled={!isComplete}
          onClick={handleSubmit}
          className={`w-full text-white text-base font-bold py-3.5 rounded-md mb-3 ${
            isComplete ? 'bg-[#19C463]' : 'bg-[#19C463] bg-opacity-40'
          }`}
        >
          {submitting ? 'Submitting...' : 'Complete'}
        </button>

        {/* Back Button */}
        <button className="w-full bg-[#19C463] text-white text-base font-bold py-3.5 rounded-md">
          Back
        </button>
      </div>
    </div>
  );
}
